/**
 * Channel entity adapter (P15.3 / #416).
 *
 * Reference table (~5-20 rows). Carries `sort_order` for hand-picked
 * display order (NOT alphabetical) + an optional `color_hex` for the
 * heatmap column header. Backend GET /channels does NOT paginate
 * (small table), but the AdminListView's pagination handles that
 * gracefully via Load-more never firing.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface Channel {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ChannelIn {
  code: string;
  name: string;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
}

export type ChannelPatch = Partial<ChannelIn>;

const columns: AdminColumn<Channel>[] = [
  {
    key: "sort_order",
    label: "Order",
    align: "right",
    sortValue: (r) => r.sort_order,
    render: (r) => (
      <span
        data-testid="channel-sort-cell"
        className="text-xs text-slate-500 tabular-nums"
      >
        {r.sort_order}
      </span>
    ),
  },
  {
    key: "color",
    label: "",
    render: (r) =>
      r.color_hex ? (
        <span
          data-testid="channel-color-swatch"
          aria-label={`color ${r.color_hex}`}
          className="inline-block w-4 h-4 rounded border border-slate-200"
          style={{ backgroundColor: r.color_hex }}
        />
      ) : (
        <span className="inline-block w-4 h-4 rounded border border-slate-200 bg-slate-50" />
      ),
  },
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "description",
    label: "Description",
    render: (r) => (
      <span className="text-xs text-slate-600 line-clamp-1">{r.description ?? ""}</span>
    ),
  },
];

const formFields: FormField<ChannelIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 32 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 128 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  { kind: "text", key: "color_hex", label: "Color hex (#RRGGBB)", maxLength: 7 },
  { kind: "number", key: "sort_order", label: "Sort order", min: 0, max: 9999 },
];

export const channelAdapter: EntityAdapter<Channel, ChannelIn, ChannelPatch> = {
  i18nPrefix: "modules.foundation.admin.channels",
  importKind: "channel",
  endpoint: "/v1/admin/foundation/channels",
  queryKey: ["foundation-admin", "channels"] as const,
  columns,
  defaultSort: { key: "sort_order", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    color_hex: null,
    sort_order: 0,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    color_hex: r.color_hex,
    sort_order: r.sort_order,
  }),
};
